import { type ReactNode, useEffect, useMemo, useState } from "react";
import { AiManagerAdvisorPanel } from "../ai/AiManagerAdvisorPanel";
import type { AiGatewayClient } from "../ai/client";
import { buildInfo } from "../core/buildInfo";
import { featureFlags } from "../core/featureFlags";
import type { HallPatrolQuickAction } from "../qr/hallPatrol";
import { formatPersianDate } from "./persianDate";
import { screenDefinition } from "./screenRegistry";
import { SettingsHub, type SettingsSurfaceId } from "./SettingsHub";
import { publicTaskTitle } from "./taskDisplay";
import {
  type AppRole,
  type ConnectivityState,
  type HomeNavItem,
  type SyncSummary,
  type WorkItem,
  navItemsForRole,
  roleLabel,
  syncStatusLabel,
  syncStatusTone,
  visibleWorkItems,
} from "./model";

export interface AppShellSnapshot {
  role: AppRole;
  userName: string;
  farmName: string;
  connectivity: ConnectivityState;
  sync: SyncSummary;
  workItems: readonly WorkItem[];
  hallPatrolActions: readonly HallPatrolQuickAction[];
  alerts: number;
  isPlatformOwner?: boolean;
}

interface AppShellProps {
  snapshot: AppShellSnapshot;
  aiClient?: AiGatewayClient;
  hallPatrolPanel?: ReactNode;
  workerOperationsPanel?: ReactNode;
  feedingPanel?: ReactNode;
  todayTaskPanel?: ReactNode;
  offlineCenterPanel?: ReactNode;
  renderSettingsPanel?: (surface: SettingsSurfaceId) => ReactNode;
  managerWorkersPanel?: ReactNode;
  managerSubscribersPanel?: ReactNode;
  managerReportsPanel?: ReactNode;
  managerFinancePanel?: ReactNode;
  managerFinanceWarehouseReportPanel?: ReactNode;
  managerWarehouseAssetPanel?: ReactNode;
  onChangeFarm?: () => void;
  onSignOut?: () => void;
}

function navLabel(item: HomeNavItem, active: boolean, onSelect: () => void) {
  return (
    <button
      key={item.id}
      type="button"
      className={active ? "bottom-nav-item active" : "bottom-nav-item"}
      aria-current={active ? "page" : undefined}
      onClick={onSelect}
    >
      <span className="nav-icon" aria-hidden="true">
        {item.icon}
      </span>
      <span>{item.label}</span>
    </button>
  );
}

function statusCopy(status: WorkItem["status"]): string {
  switch (status) {
    case "pending":
      return "در انتظار";
    case "late":
      return "با تأخیر";
    case "conflict":
      return "نیاز به بررسی";
    case "done":
      return "انجام شد";
  }
}

function WorkList({
  items,
  emptyText,
  limit,
}: {
  items: readonly WorkItem[];
  emptyText: string;
  limit?: number;
}) {
  const visible = typeof limit === "number" ? items.slice(0, limit) : items;

  if (visible.length === 0) {
    return <p className="empty-state">{emptyText}</p>;
  }

  return (
    <div className="work-list">
      {visible.map((item) => (
        <div className={`work-item ${item.status}`} key={item.id}>
          <span className="work-main">
            <strong>{publicTaskTitle(item.title)}</strong>
            <span>
              {item.tankCode ? `تانک ${item.tankCode}` : "کار سالن"}
              {item.dueLabel ? ` · ${item.dueLabel}` : ""}
            </span>
          </span>
          <span className="work-status">{statusCopy(item.status)}</span>
        </div>
      ))}
    </div>
  );
}

function PageIntro({ pageId }: { pageId: HomeNavItem["id"] }) {
  const page = screenDefinition(pageId);

  return (
    <section className="page-intro">
      {pageId !== "today" && pageId !== "finance" ? (
        <p className="section-kicker">Discus AI V2</p>
      ) : null}
      <h2>{page.label}</h2>
      <p className="section-note">{page.purpose}</p>
    </section>
  );
}

export function AppShell({
  snapshot,
  aiClient,
  hallPatrolPanel,
  workerOperationsPanel,
  feedingPanel,
  todayTaskPanel,
  offlineCenterPanel,
  renderSettingsPanel,
  managerWorkersPanel,
  managerSubscribersPanel,
  managerReportsPanel,
  managerFinancePanel,
  managerFinanceWarehouseReportPanel,
  managerWarehouseAssetPanel,
  onChangeFarm,
  onSignOut,
}: AppShellProps) {
  const [activePage, setActivePage] = useState<HomeNavItem["id"]>("home");
  const [hallOperationsOpen, setHallOperationsOpen] = useState(false);
  const [hallMoreOpen, setHallMoreOpen] = useState(false);
  const [hallOperationsIntent, setHallOperationsIntent] = useState<
    "treatment" | "daily" | "breeding" | "inventory"
  >("treatment");
  const [settingsInitialSurface, setSettingsInitialSurface] = useState<SettingsSurfaceId>();
  const visibleWork = useMemo(() => visibleWorkItems(snapshot.workItems), [snapshot.workItems]);

  useEffect(() => {
    if (!activePage) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.querySelector(".content-area")?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [activePage]);

  useEffect(() => {
    if (!hallOperationsOpen || activePage !== "hall-menu") return;
    requestAnimationFrame(() => {
      document.getElementById("worker-ops-title")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [activePage, hallOperationsOpen]);
  const navItems = navItemsForRole(snapshot.role, snapshot.isPlatformOwner === true);
  const tone = syncStatusTone(snapshot.connectivity, snapshot.sync);
  const polishedPageClasses: Partial<Record<HomeNavItem["id"], string>> = {
    home: "app-shell home-view",
    "hall-menu": "app-shell hall-menu-view",
    today: "app-shell today-view",
    finance: "app-shell finance-view",
  };

  return (
    <main className={polishedPageClasses[activePage] ?? "app-shell"} dir="rtl">
      {activePage !== "patrol" ? (
        <>
          <header className={onChangeFarm ? "topbar with-farm-switch" : "topbar"}>
            <div className="topbar-identity">
              {activePage !== "home" &&
              activePage !== "hall-menu" &&
              activePage !== "today" &&
              activePage !== "finance" ? (
                <p className="topbar-kicker">{snapshot.farmName}</p>
              ) : null}
              <h1 className="topbar-title">سلام {snapshot.userName}</h1>
              <p className="topbar-meta">
                {roleLabel(snapshot.role)} · {formatPersianDate(Date.now())}
              </p>
            </div>
            <div className="topbar-actions">
              {onChangeFarm ? (
                <button
                  type="button"
                  className="text-button topbar-change-farm"
                  onClick={onChangeFarm}
                >
                  تغییر سالن
                </button>
              ) : null}
              {onSignOut ? (
                <button type="button" className="text-button" onClick={onSignOut}>
                  خروج
                </button>
              ) : null}
              {snapshot.role !== "operator" ? (
                <button
                  type="button"
                  className={activePage === "settings" ? "icon-button active" : "icon-button"}
                  aria-label="تنظیمات"
                  title="تنظیمات"
                  onClick={() => {
                    setSettingsInitialSurface(undefined);
                    setActivePage("settings");
                  }}
                >
                  <span aria-hidden="true">⚙</span>
                </button>
              ) : null}
              <button type="button" className="icon-button" aria-label="اعلان‌ها">
                <span aria-hidden="true">🔔</span>
                {snapshot.alerts > 0 ? (
                  <span className="notification-badge">{snapshot.alerts}</span>
                ) : null}
              </button>
            </div>
          </header>

          <section className={`sync-banner ${tone}`} aria-live="polite">
            <span className="sync-dot" aria-hidden="true" />
            <strong>{syncStatusLabel(snapshot.connectivity, snapshot.sync)}</strong>
            <button type="button" className="text-button" onClick={() => setActivePage("offline")}>
              جزئیات
            </button>
          </section>
        </>
      ) : null}

      <div className="content-area">
        {activePage === "home" ? (
          <div className="home-dashboard">
            <section className="hero-card home-hero">
              <div className="home-hero-copy">
                <h2>وضعیت امروز سالن</h2>
                <p>
                  {visibleWork.length === 0
                    ? "کار بازی باقی نمانده؛ سالن برای ادامه کار آماده است."
                    : `${visibleWork.length} کار باز برای امروز باقی مانده است.`}
                </p>
              </div>
              <button
                type="button"
                className="patrol-button home-patrol-button"
                aria-label="شروع گشت سالن با اسکن QR تانک"
                onClick={() => setActivePage("patrol")}
              >
                <span aria-hidden="true">▣</span>
                <span>
                  <strong>گشت سالن</strong>
                  <small>اسکن QR تانک</small>
                </span>
              </button>
            </section>

            <section className="home-kpi-grid" aria-label="خلاصه وضعیت امروز">
              <article className="home-kpi-card info">
                <span className="home-kpi-icon" aria-hidden="true">
                  ✓
                </span>
                <strong>{visibleWork.length}</strong>
                <small>کار باز</small>
              </article>
              <article
                className={snapshot.alerts > 0 ? "home-kpi-card warning" : "home-kpi-card ok"}
              >
                <span className="home-kpi-icon" aria-hidden="true">
                  🔔
                </span>
                <strong>{snapshot.alerts}</strong>
                <small>اعلان</small>
              </article>
              <article
                className={
                  snapshot.sync.conflicts > 0 ? "home-kpi-card danger" : "home-kpi-card ok"
                }
              >
                <span className="home-kpi-icon" aria-hidden="true">
                  !
                </span>
                <strong>{snapshot.sync.conflicts}</strong>
                <small>تعارض</small>
              </article>
              <article className={`home-kpi-card connection ${snapshot.connectivity}`}>
                <span className="home-kpi-icon" aria-hidden="true">
                  ●
                </span>
                <strong>
                  {snapshot.connectivity === "online"
                    ? "آنلاین"
                    : snapshot.connectivity === "offline"
                      ? "آفلاین"
                      : "ضعیف"}
                </strong>
                <small>اتصال</small>
              </article>
            </section>

            <section className="section-card home-today-card" aria-labelledby="today-title">
              <div className="section-title-row">
                <div>
                  <p className="section-kicker">امروز</p>
                  <h2 id="today-title">کارهای باقی‌مانده</h2>
                </div>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => setActivePage("today")}
                >
                  مشاهده همه
                </button>
                <span className="count-pill">{visibleWork.length}</span>
              </div>
              <section
                className="home-today-scroll"
                aria-label="فهرست کارهای باقی‌مانده"
                tabIndex={visibleWork.length > 2 ? 0 : undefined}
              >
                <WorkList items={visibleWork} emptyText="کار باز باقی نمانده است." />
              </section>
            </section>

            <section className="section-card home-quick-section" aria-labelledby="home-ops-title">
              <div className="section-title-row">
                <div>
                  <p className="section-kicker">دسترسی سریع</p>
                  <h2 id="home-ops-title">عملیات پرکاربرد</h2>
                </div>
              </div>
              <div className="home-quick-grid">
                <button
                  type="button"
                  className="home-quick-action feeding"
                  onClick={() => setActivePage("feeding")}
                >
                  <span aria-hidden="true">🍽️</span>
                  <strong>تغذیه</strong>
                  <small>ثبت و مدیریت تغذیه</small>
                </button>

                {snapshot.role !== "operator" ? (
                  <button
                    type="button"
                    className="home-quick-action workers"
                    onClick={() => setActivePage("workers")}
                  >
                    <span aria-hidden="true">👷</span>
                    <strong>مدیریت کارگران</strong>
                    <small>نیرو و شیفت سالن</small>
                  </button>
                ) : null}

                {snapshot.role !== "operator" ? (
                  <button
                    type="button"
                    className="home-quick-action reports"
                    onClick={() => setActivePage("reports")}
                  >
                    <span aria-hidden="true">▤</span>
                    <strong>گزارش</strong>
                    <small>وضعیت و تحلیل سالن</small>
                  </button>
                ) : null}

                <button
                  type="button"
                  className="home-quick-action treatment"
                  onClick={() =>
                    document.getElementById("worker-ops-title")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                  }
                >
                  <span aria-hidden="true">🧪</span>
                  <strong>درمان</strong>
                  <small>انتخاب تانک در مرحله بعد</small>
                </button>
              </div>
            </section>

            <div className="home-operations-shell">
              {workerOperationsPanel ?? (
                <section className="section-card" aria-labelledby="quick-title">
                  <div className="section-title-row">
                    <div>
                      <p className="section-kicker">عملیات</p>
                      <h2 id="quick-title">ثبت سریع</h2>
                    </div>
                  </div>
                  <p className="section-note">پنل عملیات سالن در این محیط آماده نشده است.</p>
                </section>
              )}
            </div>

            {snapshot.role !== "operator" && snapshot.sync.conflicts > 0 ? (
              <section className="section-card manager-card home-conflict-card" aria-live="polite">
                <p className="section-kicker">خطا و تعارض</p>
                <h2>{snapshot.sync.conflicts} مورد نیاز به بررسی</h2>
                <p>تعارض‌ها پنهان نمی‌شوند و نیاز به تصمیم صریح مدیر دارند.</p>
              </section>
            ) : null}
          </div>
        ) : null}

        {activePage === "hall-menu" ? (
          <div className="hall-menu-dashboard">
            <section
              className="section-card hall-menu-actions"
              aria-labelledby="hall-menu-actions-title"
            >
              <div className="section-title-row">
                <div>
                  <p className="section-kicker">همیشه در دسترس</p>
                  <h2 id="hall-menu-actions-title">عملیات سالن</h2>
                </div>
              </div>
              <div className="quick-grid hall-menu-action-grid">
                <button
                  type="button"
                  className="quick-button hall-menu-action feeding"
                  onClick={() => setActivePage("feeding")}
                >
                  <span aria-hidden="true">🍽️</span>
                  <strong>تغذیه</strong>
                </button>

                {snapshot.role !== "operator" ? (
                  <button
                    type="button"
                    className="quick-button hall-menu-action workers"
                    onClick={() => setActivePage("workers")}
                  >
                    <span aria-hidden="true">👷</span>
                    <strong>مدیریت کارگران</strong>
                  </button>
                ) : null}

                {snapshot.role !== "operator" ? (
                  <button
                    type="button"
                    className="quick-button hall-menu-action reports"
                    onClick={() => setActivePage("reports")}
                  >
                    <span aria-hidden="true">▤</span>
                    <strong>گزارش</strong>
                  </button>
                ) : null}

                <button
                  type="button"
                  className={`quick-button hall-menu-action treatment${
                    hallOperationsOpen && hallOperationsIntent === "treatment" ? " active" : ""
                  }`}
                  aria-pressed={hallOperationsOpen && hallOperationsIntent === "treatment"}
                  onClick={() => {
                    setHallOperationsIntent("treatment");
                    setHallOperationsOpen(true);
                  }}
                >
                  <span aria-hidden="true">🧪</span>
                  <strong>درمان</strong>
                  <small>انتخاب تانک در مرحله بعد</small>
                </button>
              </div>
            </section>

            <section
              className="section-card hall-more-actions"
              aria-labelledby="hall-more-actions-title"
            >
              <button
                type="button"
                className={`hall-more-toggle${hallMoreOpen ? " active" : ""}`}
                aria-expanded={hallMoreOpen}
                aria-controls="hall-more-actions-grid"
                onClick={() => setHallMoreOpen((current) => !current)}
              >
                <span className="hall-more-toggle-copy">
                  <strong id="hall-more-actions-title">سایر عملیات سالن</strong>
                  <small>تکثیر، بچه‌ماهی، عملیات روزانه، آب و تنظیمات</small>
                </span>
                <span className="hall-more-toggle-icon" aria-hidden="true">
                  {hallMoreOpen ? "⌃" : "⌄"}
                </span>
              </button>

              {hallMoreOpen ? (
                <div className="hall-more-grid" id="hall-more-actions-grid">
                  <button
                    type="button"
                    className="hall-more-action"
                    onClick={() => {
                      setHallOperationsIntent("daily");
                      setHallOperationsOpen(true);
                    }}
                  >
                    <span aria-hidden="true">🧰</span>
                    <span>
                      <strong>عملیات روزانه</strong>
                      <small>تعویض آب، سیفون، پارامتر آب، سلامت و تلفات</small>
                    </span>
                  </button>

                  <button
                    type="button"
                    className="hall-more-action"
                    onClick={() => {
                      setHallOperationsIntent("breeding");
                      setHallOperationsOpen(true);
                    }}
                  >
                    <span aria-hidden="true">🥚</span>
                    <span>
                      <strong>تکثیر</strong>
                      <small>انتخاب تانک و سپس عملیات مرتبط با جفت</small>
                    </span>
                  </button>

                  <button
                    type="button"
                    className="hall-more-action"
                    onClick={() => {
                      setHallOperationsIntent("inventory");
                      setHallOperationsOpen(true);
                    }}
                  >
                    <span aria-hidden="true">🐠</span>
                    <span>
                      <strong>بچه‌ماهی و موجودی</strong>
                      <small>انتخاب تانک و ورود به اطلاعات گله و بچه‌ماهی</small>
                    </span>
                  </button>

                  <button
                    type="button"
                    className="hall-more-action"
                    onClick={() => setActivePage("today")}
                  >
                    <span aria-hidden="true">📋</span>
                    <span>
                      <strong>کارهای امروز</strong>
                      <small>وظایف باز، پیگیری‌ها و ثبت انجام کار</small>
                    </span>
                  </button>

                  {snapshot.role !== "operator" ? (
                    <button
                      type="button"
                      className="hall-more-action"
                      onClick={() => {
                        setSettingsInitialSurface("water-circuits");
                        setActivePage("settings");
                      }}
                    >
                      <span aria-hidden="true">💧</span>
                      <span>
                        <strong>آب و مدارهای آبی</strong>
                        <small>مدارها، اتصال تانک‌ها و پروفایل آب</small>
                      </span>
                    </button>
                  ) : null}

                  {snapshot.role !== "operator" ? (
                    <button
                      type="button"
                      className="hall-more-action"
                      onClick={() => {
                        setSettingsInitialSurface(undefined);
                        setActivePage("settings");
                      }}
                    >
                      <span aria-hidden="true">⚙️</span>
                      <span>
                        <strong>تنظیمات سالن</strong>
                        <small>تانک‌ها، برنامه غذا، QR، اعلان‌ها و دسترسی‌ها</small>
                      </span>
                    </button>
                  ) : null}
                </div>
              ) : null}
            </section>

            {hallOperationsOpen ? (
              <>
                <p className="hall-operations-context" role="status">
                  {hallOperationsIntent === "treatment"
                    ? "درمان: ابتدا تانک را انتخاب کنید؛ سپس گزینه درمان را بزنید."
                    : hallOperationsIntent === "breeding"
                      ? "تکثیر: ابتدا تانک را انتخاب کنید؛ سپس گزینه تکثیر نمایش داده می‌شود."
                      : hallOperationsIntent === "inventory"
                        ? "بچه‌ماهی و موجودی: ابتدا تانک را انتخاب کنید؛ سپس از بخش تکثیر وارد اطلاعات مربوط شوید."
                        : "عملیات روزانه: ابتدا تانک را انتخاب کنید تا عملیات مجاز همان تانک نمایش داده شود."}
                </p>
                {workerOperationsPanel ?? (
                  <section className="section-card">
                    <p className="section-note">پنل عملیات سالن در این محیط آماده نشده است.</p>
                  </section>
                )}
              </>
            ) : null}
          </div>
        ) : null}

        {activePage === "today" ? (
          <>
            <PageIntro pageId="today" />
            {feedingPanel}
            {todayTaskPanel}
            {visibleWork.some((item) => item.kind !== "task") ? (
              <section className="section-card" aria-labelledby="today-page-title">
                <div className="section-title-row">
                  <h2 id="today-page-title">سایر کارهای باز</h2>
                  <span className="count-pill">
                    {visibleWork.filter((item) => item.kind !== "task").length}
                  </span>
                </div>
                <WorkList
                  items={visibleWork.filter((item) => item.kind !== "task")}
                  emptyText="کار باز دیگری باقی نمانده است."
                />
              </section>
            ) : null}
          </>
        ) : null}

        {activePage === "feeding" ? (
          <>
            <PageIntro pageId="feeding" />
            {feedingPanel}
          </>
        ) : null}

        {activePage === "patrol"
          ? (hallPatrolPanel ?? (
              <section className="section-card hall-patrol-preview" aria-labelledby="patrol-title">
                <div className="section-title-row">
                  <div>
                    <p className="section-kicker">گشت سالن</p>
                    <h2 id="patrol-title">اکشن‌های محلی آماده</h2>
                  </div>
                </div>
                <p className="section-note">
                  دوربین پس از اسکن باز می‌ماند و فقط اکشن‌های مجاز همان تانک نمایش داده می‌شوند.
                </p>
                <div className="action-chip-list">
                  {snapshot.hallPatrolActions.length === 0 ? (
                    <span className="muted-chip">پس از اسکن تانک نمایش داده می‌شود</span>
                  ) : (
                    snapshot.hallPatrolActions.map((action) => (
                      <span className="action-chip" key={action.action}>
                        {action.action}
                      </span>
                    ))
                  )}
                </div>
              </section>
            ))
          : null}

        {activePage === "offline" ? (
          <>
            <PageIntro pageId="offline" />
            <section className="section-card">
              <div className="quick-grid">
                <article className="manager-card">
                  <p className="section-kicker">در صف</p>
                  <h2>{snapshot.sync.pending}</h2>
                </article>
                <article className="manager-card">
                  <p className="section-kicker">در حال ارسال</p>
                  <h2>{snapshot.sync.syncing}</h2>
                </article>
                <article className="manager-card">
                  <p className="section-kicker">ارسال مجدد</p>
                  <h2>{snapshot.sync.retryable}</h2>
                </article>
                <article className="manager-card">
                  <p className="section-kicker">تعارض</p>
                  <h2>{snapshot.sync.conflicts}</h2>
                </article>
              </div>
              {offlineCenterPanel ?? (
                <p className="section-note">مرکز آفلاین در این محیط آماده نشده است.</p>
              )}
            </section>
          </>
        ) : null}

        {activePage === "workers" && snapshot.role !== "operator" ? (
          <>
            <PageIntro pageId="workers" />
            {managerWorkersPanel}
          </>
        ) : null}

        {activePage === "finance" && snapshot.role !== "operator" ? (
          <>
            <PageIntro pageId="finance" />
            {managerFinanceWarehouseReportPanel}
            {managerFinancePanel}
            {managerWarehouseAssetPanel}
          </>
        ) : null}

        {activePage === "subscribers" && snapshot.role !== "operator" ? (
          <>
            <PageIntro pageId="subscribers" />
            {managerSubscribersPanel ?? (
              <section className="section-card">
                <p className="section-note">مدیریت مشترکین در حال تکمیل همین مسیر است.</p>
              </section>
            )}
          </>
        ) : null}

        {activePage === "reports" && snapshot.role !== "operator" ? (
          <>
            <PageIntro pageId="reports" />
            {featureFlags.aiUiEnabled && aiClient ? (
              <AiManagerAdvisorPanel client={aiClient} />
            ) : null}
            {managerReportsPanel}
          </>
        ) : null}

        {activePage === "settings" && snapshot.role !== "operator" ? (
          <>
            <PageIntro pageId="settings" />
            <SettingsHub
              key={settingsInitialSurface ?? "settings-root"}
              aiEnabled={featureFlags.aiUiEnabled}
              {...(settingsInitialSurface ? { initialSurface: settingsInitialSurface } : {})}
              renderPanel={(surface) =>
                renderSettingsPanel?.(surface) ?? (
                  <section className="section-card">
                    <p className="section-note">این بخش تنظیمات در این محیط آماده نشده است.</p>
                  </section>
                )
              }
            />
          </>
        ) : null}
      </div>

      <footer className="version-footer">
        <span>Discus AI V{buildInfo.version}</span>
        <span className="version-sha">{buildInfo.commitSha}</span>
      </footer>

      <nav className="bottom-nav" aria-label="منوی اصلی">
        {navItems.map((item) =>
          navLabel(item, activePage === item.id, () => setActivePage(item.id)),
        )}
      </nav>
    </main>
  );
}
